/**
 * Pure utility functions for working with transaction objects.
 *
 * No global state dependencies -- client-specific behavior (isJMS/isSheetz)
 * is accepted as an explicit `flags` parameter, never read from an ambient
 * global, so these functions stay testable in isolation.
 *
 * Cert numbers: as of #5621 (2026-07-29), per Chris ("if I had a magic wand
 * there would be only one universal path to get a cert number"), there is
 * exactly one public entry point for cert-number lookup anywhere in this
 * codebase: getCertNumber() in string-utils.js. It accepts either a raw
 * string or a transaction-like object ({notes, description}), always applies
 * the same rules for every caller (Sheetz/JMS-aware, "#" required), and
 * always returns a plain string. This file used to also export
 * extractCertificateNumber() and deriveCertNumber() as separate, similarly-
 * named entry points with subtly different contracts (string vs. object
 * input; whether a description fallback applied) -- both were folded into
 * getCertNumber() and removed from here. matchCertNumber() (below) is the
 * shared low-level regex engine getCertNumber() calls; nothing outside this
 * file should call it directly.
 *
 * A raw `tx.certNumber` field, even if present, is never trusted -- see
 * matchCertNumber's doc for why (Cert # is never legitimately-saved data).
 *
 * Usage:
 *   import { deriveDescription, getTransferDescriptionForCertParse } from './helpers/transaction-utils.js';
 *   import { getCertNumber } from './helpers/string-utils.js'; // cert numbers
 */

/**
 * Internal: matches the Asplundh-shape "bare cert" pattern — a single token of
 * 1–20 alphanumeric/dash characters with at least one digit. Used to discriminate
 * cert-shaped values from description-shaped values when both can land in tx.notes
 * across loaders.
 */
function isBareCertShape(value) {
	if (typeof value !== 'string') return false;
	const trimmed = value.trim();
	return /^(?=.*\d)[A-Z0-9-]{1,20}$/i.test(trimmed);
}

/**
 * Single canonical cert-number pattern matcher -- the regex engine behind
 * getCertNumber() (string-utils.js), the only public way to get a cert
 * number anywhere in this codebase. Nothing outside this file should call
 * matchCertNumber() directly; it exists here (rather than inlined in
 * getCertNumber()) purely so the regex rules are unit-testable in isolation
 * from client-flags.js/DOM concerns.
 *
 * "#" is the universal, required marker for an active cert-number reference,
 * across every client -- no "#" means no active cert, full stop. (E.g. in
 * "Reissuance (#65 from 42)", 65 is the active cert; 42 is a prior/inactive
 * cert reference and is correctly NOT matched, since it isn't "#"-prefixed.
 * There is no such thing as a bare/unmarked cert reference -- a client whose
 * notes are a lone token like "42" or "CB1994" with no "#" simply has no
 * cert number to extract there.)
 *
 * Recognizes two "#"-prefixed shapes:
 *   - Sheetz's un-hashed "CB-1994" / hashed "#CB-1994" shape, when
 *     flags.isSheetz -- letters, dash, digits, optional leading "#" (Sheetz
 *     is the one exception to "#" being required -- its convention omits it).
 *   - Generic "#<alphanumeric+punctuation>", when the above doesn't match.
 *     The whole token after "#" is the cert, unsplit -- including a
 *     dot-separated shape like "#A.123" or "#CS.45", which returns as one
 *     opaque token ("A.123"/"CS.45"), never truncated to just the trailing
 *     number.
 *
 * Correction (2026-07-30): a prior version of this function (inherited from
 * the original #5329 report-builder work, predating #5621) had a third,
 * "#CLASS.NUMBER" tier that split a dot-separated cert down to just the
 * post-period portion (e.g. "#A.123" -> "123"). Per Chris, directly: there
 * is no legitimate case for dropping a class/prefix segment like that -- a
 * cert of "#CS.45" is the token "CS.45", full stop, same as any other
 * dash/slash/colon-separated cert shape the generic branch already handles.
 * Removed everywhere (this file, and the mirrored backend implementation in
 * NewServerless's transaction_utils.py) rather than kept as "long-standing,
 * documented" behavior -- no real client's data was ever observed depending
 * on the split, and it silently discarded half of any cert number that did
 * use this shape.
 *
 * @param {string} text
 * @param {Object} [flags]
 * @param {boolean} [flags.isSheetz=false] - use Sheetz's un-hashed letter-dash-digit pattern
 * @param {boolean} [flags.isJMS=false] - JM Search never has legitimate cert
 *   numbers; suppress unconditionally, regardless of what the text contains
 * @param {boolean} [flags.broadCharset=false] - allow "/", ":", "|", "_" in the
 *   generic (non-Sheetz) branch
 * @param {boolean} [flags.stripCommas=false] - strip "," before matching (e.g.
 *   "#123,456" -> "#123456")
 * @returns {RegExpMatchArray|null}
 */
export function matchCertNumber(text, flags = {}) {
	if (!text || flags.isJMS) return null;
	const clean = flags.stripCommas ? String(text).replace(/,/g, '') : String(text);

	if (flags.isSheetz) {
		// GH: optional trailing letter for not-yet-consolidated split certs (CB-1640A).
		return clean.match(/(?:#)?([A-Za-z]{1,5}-\d{1,20}[A-Za-z]?)(?!\w)/i);
	}

	const genericPattern = flags.broadCharset ? /#([A-Za-z0-9/:|._-]{1,20})(?!\w)/ : /#([A-Z0-9-]+)/i;
	return clean.match(genericPattern);
}

/**
 * Same pattern rules as matchCertNumber, but every match, not just the first --
 * for getNextCertNumber's "highest cert ever issued" scan, where a transaction
 * can mention more than one (Sheetz's "from X (now Y) to Z").
 * @returns {string[]} cert numbers found, in the order they appear in text.
 */
export function matchAllCertNumbers(text, flags = {}) {
	if (!text || flags.isJMS) return [];
	const clean = flags.stripCommas ? String(text).replace(/,/g, '') : String(text);

	const pattern = flags.isSheetz
		? /(?:#)?([A-Za-z]{1,5}-\d{1,20}[A-Za-z]?)(?!\w)/gi
		: flags.broadCharset
			? /#([A-Za-z0-9/:|._-]{1,20})(?!\w)/g
			: /#([A-Z0-9-]+)/gi;
	return [...clean.matchAll(pattern)].map((m) => m[1]);
}

/**
 * Derives the canonical description for a transaction object across loader shapes.
 * Resolution order:
 *   1. Explicit `tx.description` when present (external CSV with a description column).
 *   2. `tx.notes` when it does NOT look like a bare cert string. Embedded clients
 *      (`src/shared_assets/index.js:3773` and friends) write description text into
 *      `tx.notes`; falling back here recovers their description data without
 *      reintroducing the Asplundh "cert as description" conflation.
 *
 * Returns '' when only cert-shaped data is available — honest empty for clients
 * whose source data has no description column.
 *
 * @param {Object} tx - Transaction object (any loader shape).
 * @returns {string} The description text, or '' when none can be derived.
 */
export function deriveDescription(tx) {
	if (!tx) return '';
	if (tx.description) return String(tx.description);
	if (typeof tx.notes !== 'string') return '';
	const trimmed = tx.notes.trim();
	if (!trimmed) return '';
	if (isBareCertShape(trimmed)) return '';
	return tx.notes;
}

/**
 * Text used to parse Transfer (...) sender/receiver certificate numbers.
 * Prefers explicit description, then notes when it contains a Transfer clause
 * (ledger stores transfer prose in notes; ASPT CSV keeps bare cert in notes).
 */
export function getTransferDescriptionForCertParse(tx) {
	if (!tx) return '';
	const description = tx.description ? String(tx.description).trim() : '';
	if (/Transfer\s*\(/i.test(description)) return description;
	const notes = typeof tx.notes === 'string' ? tx.notes.trim() : '';
	if (/Transfer\s*\(/i.test(notes)) return notes;
	return description || notes || '';
}
